/**
 * TicketFormDrawer — Drawer-based form for creating and editing tickets.
 *
 * Pattern reference: LeadFormDrawer.tsx (crm/leads)
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Drawer, Button, Select, Textarea, Input } from '@/shared/components/ui';
import { RichTextEditor } from '@/shared/components/ui/RichTextEditor';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { useTeamsDropdown } from '@/modules/teams/hooks/useTeams';
import { useChannelsDropdown } from '@/modules/channels/hooks/useChannelsDropdown';
import { useCreateTicket, useUpdateTicket, useTransferTicket, useTicketAssigneesDropdown } from '../hooks/useTickets';
import { ContactSearchCombobox } from '@/modules/crm/leads/components/ContactSearchCombobox';
import { AddContactDialog } from '@/modules/chat/components';
import type { ContactFormData } from '@/modules/chat/components';
import { useCreateContact } from '@/modules/chat/hooks/useContacts';
import type { ContactDropdownItem } from '@/modules/contacts/hooks/useContactsDropdown';
import { FollowerSelector } from './FollowerSelector';
import type { FollowerItem } from './FollowerSelector';
import type { TicketResponse, TicketPriority, PopulatedTicket } from '../types';
import { TICKET_PRIORITY } from '../types';
import { Icon } from '@/shared/icons'
// ═══════════════════════════════════════════════════════
// ENUM OPTIONS (PT-BR)
// ═══════════════════════════════════════════════════════

const CATEGORY_OPTIONS = [
  { value: 'support', label: 'Suporte Técnico' },
  { value: 'billing', label: 'Financeiro' },
  { value: 'sales', label: 'Comercial' },
  { value: 'feature', label: 'Solicitação' },
  { value: 'bug', label: 'Bug/Erro' },
  { value: 'other', label: 'Outros' },
];

const SOURCE_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'phone', label: 'Telefone' },
  { value: 'chat', label: 'Chat' },
  { value: 'portal', label: 'Portal' },
  { value: 'manual', label: 'Manual' },
  { value: 'api', label: 'API' },
];

// ═══════════════════════════════════════════════════════
// PROPS
// ═══════════════════════════════════════════════════════

export interface TicketFormDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  ticket?: TicketResponse | null;
  onSuccess?: () => void;
  /** Pre-selected contact (e.g. when creating a ticket from chat) */
  initialContact?: ContactDropdownItem | null;
  /** Pipeline ID to associate the ticket with (from the currently viewed pipeline) */
  pipelineId?: string;
}

// ═══════════════════════════════════════════════════════
// SECTION HEADER (COLLAPSIBLE)
// ═══════════════════════════════════════════════════════

interface SectionHeaderProps {
  title: string;
  expanded: boolean;
  onToggle: () => void;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ title, expanded, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className="w-full flex items-center justify-between py-1 mb-3"
  >
    <h3
      className="text-xs font-semibold uppercase tracking-wider"
      style={{ color: '#64748b' }}
    >
      {title}
    </h3>
    {expanded
      ? <Icon name="chevron-up" className="w-4 h-4" style={{ color: '#94a3b8' }} />
      : <Icon name="chevron-down" className="w-4 h-4" style={{ color: '#94a3b8' }} />
    }
  </button>
);

// Non-collapsible section title
const SectionTitle: React.FC<{ title: string }> = ({ title }) => (
  <h3
    className="text-xs font-semibold uppercase tracking-wider mb-3"
    style={{ color: '#64748b' }}
  >
    {title}
  </h3>
);

// ═══════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════

export const TicketFormDrawer: React.FC<TicketFormDrawerProps> = ({
  isOpen,
  onClose,
  mode,
  ticket,
  onSuccess,
  initialContact,
  pipelineId,
}) => {
  const isEdit = mode === 'edit';

  // ─── Contact state (create mode only) ───
  const [selectedContact, setSelectedContact] = useState<ContactDropdownItem | null>(null);

  // ─── AddContactDialog state ───
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);

  // ─── Form state ───
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<string>('medium');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [teamId, setTeamId] = useState('');
  const [followerItems, setFollowerItems] = useState<FollowerItem[]>([]);
  const [dueDate, setDueDate] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [source, setSource] = useState('');
  const [channelId, setChannelId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // ─── Collapsible sections ───
  const [assignmentExpanded, setAssignmentExpanded] = useState(true);
  const [followersExpanded, setFollowersExpanded] = useState(true);
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  // ─── Hooks de dropdown ───
  // Responsável: só quem participa do pipeline do ticket (qualquer papel).
  // Sem pipeline conhecido, todos os usuários ativos (comportamento anterior).
  const formPipelineId = (isEdit ? ticket?.pipelineId : undefined) || pipelineId;
  const { data: pipelineAssignees } = useTicketAssigneesDropdown(formPipelineId, { enabled: isOpen });
  const { data: allActiveUsers } = useUsersDropdown({ status: 'active', enabled: isOpen && !formPipelineId });
  const users = (formPipelineId ? pipelineAssignees : allActiveUsers) ?? [];
  const { data: teamsData } = useTeamsDropdown();
  const { data: channelsData } = useChannelsDropdown();

  // ─── Mutations ───
  const createTicketMutation = useCreateTicket();
  const updateTicketMutation = useUpdateTicket();
  const transferTicketMutation = useTransferTicket();
  const createContactMutation = useCreateContact();

  // ─── Populate form for edit mode ───
  useEffect(() => {
    if (isEdit && ticket) {
      setTitle(ticket.title || '');
      setDescription(ticket.description || '');
      setPriority(ticket.priority || 'medium');
      setCategory(ticket.category || '');
      setTags(ticket.tags || []);
      setAssigneeId(ticket.assigneeId || '');
      setTeamId(ticket.teamId || '');
      setDueDate(ticket.dueDate ? ticket.dueDate.substring(0, 10) : '');
      setInternalNotes(ticket.internalNotes || '');
      setSource(ticket.source || '');
      setChannelId(ticket.channelId || '');
      // Followers are managed separately in edit mode
      setFollowerItems([]);
    }
  }, [ticket, isEdit]);

  // ─── Reset form when opening in create mode ───
  useEffect(() => {
    if (isOpen && !isEdit) {
      setSelectedContact(initialContact || null);
      setTitle('');
      setDescription('');
      setPriority('medium');
      setCategory('');
      setTags([]);
      setTagInput('');
      setAssigneeId('');
      setTeamId('');
      setFollowerItems([]);
      setDueDate('');
      setInternalNotes('');
      setSource('');
      setChannelId('');
      setFieldErrors({});

      // Reset collapsed sections
      setAssignmentExpanded(true);
      setFollowersExpanded(true);
      setDetailsExpanded(false);
    }
  }, [isOpen, isEdit]);

  // Reset fieldErrors when opening in edit mode
  useEffect(() => {
    if (isOpen && isEdit) {
      setFieldErrors({});
    }
  }, [isOpen, isEdit]);

  // ─── Handler to create contact via AddContactDialog ───
  const handleRequestCreateContact = (_prefillName: string) => {
    setIsAddContactOpen(true);
  };

  const handleSaveNewContact = (contactData: ContactFormData, avatar?: File) => {
    createContactMutation.mutate(
      { data: contactData, avatar },
      {
        onSuccess: (newContact) => {
          const contactItem: ContactDropdownItem = {
            id: (newContact as unknown as { id: string }).id,
            name: (newContact as unknown as { name: string }).name,
            email: (newContact as unknown as { identifiers?: { email?: string[] } }).identifiers?.email?.[0] || undefined,
            phone: (newContact as unknown as { identifiers?: { phone?: string[] } }).identifiers?.phone?.[0] || undefined,
          };
          setSelectedContact(contactItem);
          setIsAddContactOpen(false);
          toast.success('Contato criado com sucesso!');
        },
        onError: (error: any) => {
          if (error.response?.status === 402 || error.response?.status === 403) return;
          toast.error('Erro ao criar contato. Tente novamente.');
        },
      }
    );
  };

  // ─── Field → section mapping (auto-expand on error) ───
  type SectionKey = 'assignment' | 'followers' | 'details';
  const FIELD_SECTION_MAP: Record<string, SectionKey> = {
    assigneeId: 'assignment',
    teamId: 'assignment',
    dueDate: 'details',
    internalNotes: 'details',
    source: 'details',
    channelId: 'details',
  };

  // ─── Parse backend validation errors ───
  const parseBackendErrors = (error: unknown): boolean => {
    if (!axios.isAxiosError(error)) return false;

    const data = error.response?.data;
    if (!data?.errors || typeof data.errors !== 'object') return false;

    const parsed: Record<string, string> = {};
    const sectionsToExpand = new Set<string>();

    for (const [field, messages] of Object.entries(data.errors)) {
      if (Array.isArray(messages) && messages.length > 0) {
        parsed[field] = messages[0] as string;
      } else if (typeof messages === 'string') {
        parsed[field] = messages;
      }

      const section = FIELD_SECTION_MAP[field];
      if (section) sectionsToExpand.add(section);
    }

    if (Object.keys(parsed).length === 0) return false;

    setFieldErrors(parsed);

    if (sectionsToExpand.has('assignment')) setAssignmentExpanded(true);
    if (sectionsToExpand.has('followers')) setFollowersExpanded(true);
    if (sectionsToExpand.has('details')) setDetailsExpanded(true);

    return true;
  };

  // ─── Clear error for a specific field ───
  const clearFieldError = (field: string) => {
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // ─── Tags handling ───
  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags(prev => [...prev, trimmed]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(prev => prev.filter(t => t !== tag));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  // ─── Validation ───
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    const sectionsToExpand = new Set<string>();

    if (!title.trim()) {
      errors.title = 'Titulo e obrigatorio';
    } else if (title.trim().length < 3) {
      errors.title = 'Titulo deve ter pelo menos 3 caracteres';
    }

    if (!category) {
      errors.category = 'Categoria e obrigatoria';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);

      // Auto-expand sections with errors
      for (const field of Object.keys(errors)) {
        const section = FIELD_SECTION_MAP[field];
        if (section) sectionsToExpand.add(section);
      }
      if (sectionsToExpand.has('assignment')) setAssignmentExpanded(true);
      if (sectionsToExpand.has('details')) setDetailsExpanded(true);

      return false;
    }

    return true;
  };

  // ─── Submit ───
  const handleSubmit = async () => {
    setFieldErrors({});

    if (!validateForm()) return;

    if (isEdit) {
      // ── Edit mode ──
      if (!ticket) return;

      try {
        setIsLoading(true);

        // Atualizar campos não relacionados a atribuição via PUT /tickets/:id.
        // assigneeId/teamId NÃO podem ser enviados aqui — o backend aplica
        // `.strict()` no Zod e retorna 422. Transferência vai em chamada separada.
        await updateTicketMutation.mutateAsync({
          id: ticket.id,
          title: title.trim(),
          description: description.trim() || undefined,
          priority: (priority as TicketPriority) || undefined,
          category: category || undefined,
          tags: tags.length > 0 ? tags : undefined,
          dueDate: dueDate || undefined,
          internalNotes: internalNotes.trim() || undefined,
        });

        // Se responsável ou equipe mudaram, disparar transfer padronizado.
        // null desatribui, string atribui. Não altera o campo se ficou igual.
        const normalizedAssigneeId = assigneeId || null;
        const normalizedTeamId = teamId || null;
        const currentAssigneeId = ticket.assigneeId || null;
        const currentTeamId = ticket.teamId || null;

        if (normalizedAssigneeId !== currentAssigneeId) {
          await transferTicketMutation.mutateAsync({
            ticketId: ticket.id,
            body: { toUserId: normalizedAssigneeId },
          });
        }
        if (normalizedTeamId !== currentTeamId) {
          await transferTicketMutation.mutateAsync({
            ticketId: ticket.id,
            body: { toTeamId: normalizedTeamId },
          });
        }

        toast.success('Ticket atualizado com sucesso!');
        onSuccess?.();
        onClose();
      } catch (error) {
        console.error('Erro ao editar ticket:', error);
        if (!parseBackendErrors(error)) {
          toast.error('Erro ao editar ticket. Tente novamente.');
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      // ── Create mode ──
      try {
        setIsLoading(true);

        const requestBody = {
          title: title.trim(),
          description: description.trim() || undefined,
          priority: (priority as TicketPriority) || 'medium',
          category,
          tags: tags.length > 0 ? tags : undefined,
          pipelineId: pipelineId || undefined,
          contactId: selectedContact?.id || undefined,
          assigneeId: assigneeId || undefined,
          teamId: teamId || undefined,
          channelId: channelId || undefined,
          source: source || undefined,
          dueDate: dueDate || undefined,
          internalNotes: internalNotes.trim() || undefined,
          followers: followerItems.map(f => ({
            followerType: f.type as 'user' | 'contact',
            followerId: f.id,
            source: 'manual' as const,
          })),
        };

        await createTicketMutation.mutateAsync(requestBody as Parameters<typeof createTicketMutation.mutateAsync>[0]);

        toast.success('Ticket criado com sucesso!');
        onSuccess?.();
        onClose();
      } catch (error) {
        console.error('Erro ao criar ticket:', error);
        if (!parseBackendErrors(error)) {
          toast.error('Erro ao criar ticket. Tente novamente.');
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  // ─── Options ───
  const userOptions = users.map(u => ({ value: u.id, label: u.name }));
  // Edição: o responsável gravado pode não participar do pipeline (dado
  // antigo) — continua na lista para o campo não aparecer vazio.
  const originalAssigneeId = isEdit ? ticket?.assigneeId : undefined;
  if (originalAssigneeId && assigneeId === originalAssigneeId && !userOptions.some(o => o.value === originalAssigneeId)) {
    userOptions.push({ value: originalAssigneeId, label: 'Responsável atual (fora do pipeline)' });
  }
  const teamOptions = (teamsData || []).map((t: { id: string; name: string }) => ({ value: t.id, label: t.name }));
  const channelOptions = (channelsData || []).map((c: { id: string; name: string }) => ({ value: c.id, label: c.name }));

  // ─── Validation state ───
  const canSubmit = title.trim().length >= 3 && !!category && !isLoading;

  // ─── Contact info for edit mode ───
  const populatedTicket = ticket as unknown as PopulatedTicket | null;
  const contactName = populatedTicket?.contact?.name || 'Sem contato';
  const contactEmail = (populatedTicket as unknown as { contact?: { email?: string; identifiers?: { email?: string[] } } })?.contact?.identifiers?.email?.[0]
    || (populatedTicket as unknown as { contact?: { email?: string } })?.contact?.email;
  const contactPhone = (populatedTicket as unknown as { contact?: { phone?: string; identifiers?: { phone?: string[] } } })?.contact?.identifiers?.phone?.[0]
    || (populatedTicket as unknown as { contact?: { phone?: string } })?.contact?.phone;

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        icon={isEdit ? <Icon name="edit" className="w-5 h-5" /> : <Icon name="file-text" className="w-5 h-5" />}
        title={isEdit ? 'Editar Ticket' : 'Novo Ticket'}
        subtitle={isEdit ? `#${ticket?.ticketNumber || ''}` : undefined}
        disabled={isLoading}
        footer={
          <>
            <Button
              onClick={onClose}
              variant="ghost"
              size="md"
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              variant="primary"
              size="md"
              disabled={!canSubmit}
            >
              {isLoading
                ? (isEdit ? 'Salvando...' : 'Criando...')
                : (isEdit ? 'Salvar' : 'Criar')
              }
            </Button>
          </>
        }
      >
        <div className="space-y-5 px-6 py-5">
          {/* ═══ SECTION 1: CONTATO (non-collapsible) ═══ */}
          <div>
            <SectionTitle title="Contato" />
            {isEdit ? (
              /* Read-only contact card */
              <div
                className="flex items-center gap-3 px-4 py-3 rounded-lg"
                style={{
                  backgroundColor: '#f9fafb',
                  border: '1px solid #e5e7eb',
                }}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: '#e0e7ff', color: '#4f46e5' }}
                >
                  <Icon name="user" className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: '#1e293b' }}>
                    {contactName}
                  </p>
                  <div className="flex items-center gap-2 text-xs" style={{ color: '#64748b' }}>
                    {contactEmail && <span className="truncate">{contactEmail}</span>}
                    {contactEmail && contactPhone && <span>·</span>}
                    {contactPhone && <span className="truncate">{contactPhone}</span>}
                  </div>
                </div>
              </div>
            ) : (
              /* Searchable combobox */
              <ContactSearchCombobox
                value={selectedContact}
                onChange={setSelectedContact}
                onRequestCreateContact={handleRequestCreateContact}
                disabled={isLoading}
              />
            )}
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid #e5e7eb' }} />

          {/* ═══ SECTION 2: INFORMACOES DO TICKET (non-collapsible) ═══ */}
          <div>
            <SectionTitle title="Informações do Ticket" />
            <div className="space-y-3">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: '#1e293b' }}>
                  Título <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <Input
                  type="text"
                  value={title}
                  onChange={(e) => { setTitle(e.target.value); clearFieldError('title'); }}
                  placeholder="Ex: Problema ao acessar o sistema"
                  disabled={isLoading}
                  error={fieldErrors.title}
                />
              </div>

              {/* Description (Rich Text) */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: '#1e293b' }}>
                  Descrição
                </label>
                <RichTextEditor
                  value={description}
                  onChange={(html) => { setDescription(html); clearFieldError('description'); }}
                  placeholder="Descreva o problema ou solicitação..."
                  minHeight={150}
                  disabled={isLoading}
                  features={{
                    bold: true,
                    italic: true,
                    underline: true,
                    bulletList: true,
                    orderedList: true,
                    link: true,
                    attachment: true,
                    heading: false,
                    image: false,
                    textAlign: false,
                  }}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    overflow: 'hidden',
                  }}
                />
                {fieldErrors.description && (
                  <p className="mt-1 text-xs" style={{ color: '#ef4444' }}>
                    {fieldErrors.description}
                  </p>
                )}
              </div>

              {/* Priority (visual buttons) */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: '#1e293b' }}>
                  Prioridade
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {Object.entries(TICKET_PRIORITY).map(([key, config]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => { setPriority(key); clearFieldError('priority'); }}
                      className="flex flex-col items-center gap-1 px-2 py-2 rounded-lg border-2 transition-all"
                      style={{
                        borderColor: priority === key ? config.color : '#e5e7eb',
                        backgroundColor: priority === key ? `${config.color}10` : '#ffffff',
                      }}
                      disabled={isLoading}
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: config.color }}
                      />
                      <span
                        className="text-xs font-medium"
                        style={{
                          color: priority === key ? config.color : '#64748b',
                        }}
                      >
                        {config.label}
                      </span>
                    </button>
                  ))}
                </div>
                {fieldErrors.priority && (
                  <p className="mt-1 text-xs" style={{ color: '#ef4444' }}>
                    {fieldErrors.priority}
                  </p>
                )}
              </div>

              {/* Category and Tags row */}
              <div className="grid grid-cols-2 gap-3">
                <Select
                  label="Categoria"
                  required
                  value={category}
                  onChange={(v) => { setCategory(v); clearFieldError('category'); }}
                  placeholder="Selecione"
                  options={CATEGORY_OPTIONS}
                  disabled={isLoading}
                  error={fieldErrors.category}
                />
                {/* Empty column — tags below */}
                <div />
              </div>

              {/* Tags */}
              <div>
                <label
                  className="flex items-center gap-1 text-sm font-medium mb-1.5"
                  style={{ color: '#1e293b' }}
                >
                  <Icon name="tag" className="w-4 h-4" />
                  Tags
                </label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    placeholder="Digite e pressione Enter"
                    disabled={isLoading}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={handleAddTag}
                    disabled={!tagInput.trim() || isLoading}
                  >
                    Adicionar
                  </Button>
                </div>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {tags.map(tag => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full"
                        style={{
                          backgroundColor: 'rgba(139, 92, 246, 0.1)',
                          color: '#8B5CF6',
                        }}
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="hover:opacity-70"
                        >
                          <Icon name="x" className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid #e5e7eb' }} />

          {/* ═══ SECTION 3: ATRIBUICAO (collapsible, open by default) ═══ */}
          <div>
            <SectionHeader
              title="Atribuição"
              expanded={assignmentExpanded}
              onToggle={() => setAssignmentExpanded(!assignmentExpanded)}
            />
            {assignmentExpanded && (
              <div className="grid grid-cols-2 gap-3">
                <Select
                  label="Responsável"
                  value={assigneeId}
                  onChange={(v) => { setAssigneeId(v); clearFieldError('assigneeId'); }}
                  placeholder="Selecione"
                  options={userOptions}
                  disabled={isLoading}
                  error={fieldErrors.assigneeId}
                />
                <Select
                  label="Equipe"
                  value={teamId}
                  onChange={(v) => { setTeamId(v); clearFieldError('teamId'); }}
                  placeholder="Selecione"
                  options={teamOptions}
                  disabled={isLoading}
                  error={fieldErrors.teamId}
                />
              </div>
            )}
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid #e5e7eb' }} />

          {/* ═══ SECTION 4: SEGUIDORES / CC (collapsible, open by default) ═══ */}
          <div>
            <SectionHeader
              title="Seguidores / CC"
              expanded={followersExpanded}
              onToggle={() => setFollowersExpanded(!followersExpanded)}
            />
            {followersExpanded && (
              <FollowerSelector
                value={followerItems}
                onChange={setFollowerItems}
                disabled={isLoading || isEdit}
              />
            )}
            {followersExpanded && isEdit && (
              <p className="mt-2 text-xs" style={{ color: '#94a3b8' }}>
                Seguidores são gerenciados na página do ticket.
              </p>
            )}
          </div>

          {/* Divider */}
          <div style={{ borderTop: '1px solid #e5e7eb' }} />

          {/* ═══ SECTION 5: DETALHES ADICIONAIS (collapsible, closed by default) ═══ */}
          <div>
            <SectionHeader
              title="Detalhes Adicionais"
              expanded={detailsExpanded}
              onToggle={() => setDetailsExpanded(!detailsExpanded)}
            />
            {detailsExpanded && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  {/* Due Date */}
                  <div>
                    <label className="block text-sm font-medium mb-1.5" style={{ color: '#1e293b' }}>
                      Data de Vencimento
                    </label>
                    <Input
                      type="date"
                      value={dueDate}
                      onChange={(e) => { setDueDate(e.target.value); clearFieldError('dueDate'); }}
                      disabled={isLoading}
                      error={fieldErrors.dueDate}
                    />
                  </div>
                  {/* Source */}
                  <Select
                    label="Fonte"
                    value={source}
                    onChange={(v) => { setSource(v); clearFieldError('source'); }}
                    placeholder="Selecione"
                    options={SOURCE_OPTIONS}
                    disabled={isLoading}
                    error={fieldErrors.source}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {/* Channel */}
                  <Select
                    label="Canal"
                    value={channelId}
                    onChange={(v) => { setChannelId(v); clearFieldError('channelId'); }}
                    placeholder="Selecione"
                    options={channelOptions}
                    disabled={isLoading}
                    error={fieldErrors.channelId}
                  />
                  <div />
                </div>

                {/* Internal Notes */}
                <Textarea
                  label="Notas Internas"
                  value={internalNotes}
                  onChange={(e) => { setInternalNotes(e.target.value); clearFieldError('internalNotes'); }}
                  placeholder="Notas visíveis apenas para a equipe..."
                  rows={3}
                  resize="vertical"
                  disabled={isLoading}
                  error={fieldErrors.internalNotes}
                />
              </div>
            )}
          </div>
        </div>
      </Drawer>

      {/* AddContactDialog — rendered OUTSIDE the Drawer for correct z-index */}
      {!isEdit && (
        <AddContactDialog
          isOpen={isAddContactOpen}
          onClose={() => setIsAddContactOpen(false)}
          onSave={handleSaveNewContact}
          isLoading={createContactMutation.isPending}
        />
      )}
    </>
  );
};

TicketFormDrawer.displayName = 'TicketFormDrawer';
